
import React from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import ZohoCRMScript from "../ZohoCRMScript";

const XDRImplementationSection: React.FC = () => {
  return (
    <>
      <ZohoCRMScript />
      <div className="max-w-4xl mx-auto">
      <h2 className="text-3xl font-bold mb-5" id="implementation">Best Practices for Implementation</h2>
      <p className="text-lg text-gray-700 mb-6">
        A successful AI-XDR implementation requires careful planning and execution. Follow these best 
        practices to maximize your deployment's effectiveness:
      </p>
      
      <div className="space-y-6 mb-10">
        <div className="bg-slate-50 p-6 rounded-lg">
          <h3 className="text-xl font-bold mb-3">1. Assess Your Security Maturity</h3>
          <p className="mb-4">
            Before implementation, evaluate your organization's current security capabilities, processes, and personnel. 
            This assessment will help identify gaps and prioritize which XDR capabilities to implement first.
          </p>
          <ul className="space-y-2">
            <li className="flex items-start">
              <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
              <span>Document your existing security controls and their effectiveness</span>
            </li>
            <li className="flex items-start">
              <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
              <span>Identify visibility gaps across your environment</span>
            </li>
            <li className="flex items-start">
              <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
              <span>Review recent incidents to identify detection and response shortcomings</span>
            </li>
          </ul>
        </div>
        
        <div className="bg-slate-50 p-6 rounded-lg">
          <h3 className="text-xl font-bold mb-3">2. Define Clear Success Criteria</h3>
          <p className="mb-4">
            Establish measurable objectives for your XDR implementation to track progress and demonstrate value.
          </p>
          <ul className="space-y-2">
            <li className="flex items-start">
              <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
              <span>Set baseline metrics for detection time, response time, and false positive rates</span>
            </li>
            <li className="flex items-start">
              <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
              <span>Define operational efficiency targets for security team workloads</span>
            </li>
            <li className="flex items-start">
              <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
              <span>Establish risk reduction goals aligned with business objectives</span>
            </li>
          </ul>
        </div>
        
        <div className="bg-slate-50 p-6 rounded-lg">
          <h3 className="text-xl font-bold mb-3">3. Implement in Phases</h3>
          <p className="mb-4">
            A phased approach reduces complexity and allows for proper validation at each stage.
          </p>
          <ul className="space-y-2">
            <li className="flex items-start">
              <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
              <span>Start with core detection capabilities across priority assets</span>
            </li>
            <li className="flex items-start">
              <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
              <span>Gradually expand data sources and monitoring scope</span>
            </li>
            <li className="flex items-start">
              <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
              <span>Introduce automated response after building confidence in detection accuracy</span>
            </li>
          </ul>
        </div>
        
        <div className="bg-slate-50 p-6 rounded-lg">
          <h3 className="text-xl font-bold mb-3">4. Focus on Data Quality</h3>
          <p className="mb-4">
            AI systems are only as good as the data they analyze. Ensure your data collection is comprehensive and accurate.
          </p>
          <ul className="space-y-2">
            <li className="flex items-start">
              <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
              <span>Validate telemetry collection from all critical systems</span>
            </li>
            <li className="flex items-start">
              <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
              <span>Implement consistent logging standards across the environment</span>
            </li>
            <li className="flex items-start">
              <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
              <span>Regularly audit data gaps and collection failures</span>
            </li>
          </ul>
        </div>
        
        <div className="bg-slate-50 p-6 rounded-lg">
          <h3 className="text-xl font-bold mb-3">5. Develop Team Skills</h3>
          <p className="mb-4">
            Even with AI automation, skilled personnel remain essential for effective XDR operations.
          </p>
          <ul className="space-y-2">
            <li className="flex items-start">
              <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
              <span>Train security teams on XDR capabilities and investigation workflows</span>
            </li>
            <li className="flex items-start">
              <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
              <span>Develop understanding of AI concepts to effectively interpret results</span>
            </li>
            <li className="flex items-start">
              <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
              <span>Create knowledge transfer mechanisms within the security team</span>
            </li>
          </ul>
        </div>
      </div>
      
      <p className="text-lg text-gray-700 mb-8">
        While implementing AI-XDR requires careful planning, appropriate resources, and organizational 
        adaptation, the security benefits far outweigh the investment for organizations facing today's 
        threat landscape. The question is no longer whether to adopt AI-XDR, but how quickly it can be 
        effectively implemented.
      </p>
      
      <div className="bg-[#8932c8]/10 p-6 rounded-lg border border-[#8932c8]/20 mb-10">
        <h3 className="text-xl font-bold mb-4">Next Steps</h3>
        <p className="mb-6">
          Ready to explore how AI-based XDR can transform your organization's security posture? 
          Spharaka offers industry-leading XDR solutions powered by advanced AI capabilities.
        </p>
        <div className="flex flex-col sm:flex-row gap-4">
          <Dialog>
            <DialogTrigger asChild>
              <Button className="bg-[#8932c8] hover:bg-[#7a2cb3]">
                Request a Demo
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Request a Demo</DialogTitle>
              </DialogHeader>
              <div id='crmWebToEntityForm' className='zcwf_lblLeft crmWebToEntityForm' style={{backgroundColor: 'white', color: 'black', maxWidth: '600px'}}>
                <form id='webform966770000000572001' action='https://crm.zoho.in/crm/WebToLeadForm' name='WebToLeads966770000000572001' method='POST' onSubmit={() => { return (window as any).checkMandatory966770000000572001(); }} acceptCharset='UTF-8'>
                  <input type='text' style={{display:'none'}} name='xnQsjsdp' value='2eaa648798ce23a7a9a9fdfb797621a05074c8311ce9031824d1b4d79807096f' />
                  <input type='hidden' name='zc_gad' id='zc_gad' value='' />
                  <input type='text' style={{display:'none'}} name='xmIwtLD' value='3b367f412ccfde6c347aca9059e7efedf4cd6eef72f9a51d2beb75a4850be285503386a2c820cee984c68e0e4df8b7e1' />
                  <input type='text' style={{display:'none'}} name='actionType' value='TGVhZHM=' />
                  <input type='text' style={{display:'none'}} name='returnURL' value='null' />
                  
                  <div className='zcwf_row'>
                    <div className='zcwf_col_lab' style={{fontSize:'12px', fontFamily: 'Arial'}}>
                      <label htmlFor='First_Name'>First Name <span style={{color:'red'}}>*</span></label>
                    </div>
                    <div className='zcwf_col_fld'>
                      <input type='text' id='First_Name' aria-required='true' aria-label='First Name' name='First Name' maxLength={40} />
                    </div>
                  </div>
                  
                  <div className='zcwf_row'>
                    <div className='zcwf_col_lab' style={{fontSize:'12px', fontFamily: 'Arial'}}>
                      <label htmlFor='Last_Name'>Last Name <span style={{color:'red'}}>*</span></label>
                    </div>
                    <div className='zcwf_col_fld'>
                      <input type='text' id='Last_Name' aria-required='true' aria-label='Last Name' name='Last Name' maxLength={80} />
                    </div>
                  </div>
                  
                  <div className='zcwf_row'>
                    <div className='zcwf_col_lab' style={{fontSize:'12px', fontFamily: 'Arial'}}>
                      <label htmlFor='Email'>Work Email <span style={{color:'red'}}>*</span></label>
                    </div>
                    <div className='zcwf_col_fld'>
                      <input type='text' data-ftype='email' autoComplete='false' id='Email' aria-required='true' aria-label='Email' name='Email' maxLength={100} />
                    </div>
                  </div>
                  
                  <div className='zcwf_row'>
                    <div className='zcwf_col_lab' style={{fontSize:'12px', fontFamily: 'Arial'}}>
                      <label htmlFor='Company'>Company <span style={{color:'red'}}>*</span></label>
                    </div>
                    <div className='zcwf_col_fld'>
                      <input type='text' id='Company' aria-required='true' aria-label='Company' name='Company' maxLength={200} />
                    </div>
                  </div>
                  
                  <div className='zcwf_row'>
                    <div className='zcwf_col_lab' style={{fontSize:'12px', fontFamily: 'Arial'}}>
                      <label htmlFor='Description'>How can we help? <span style={{color:'red'}}>*</span></label>
                    </div>
                    <div className='zcwf_col_fld'>
                      <textarea style={{fontFamily: 'Arial, sans-serif'}} id='Description' aria-required='true' aria-label='Description' name='Description'></textarea>
                    </div>
                  </div>
                  
                  <div className='zcwf_row'>
                    <div className='zcwf_col_lab'></div>
                    <div className='zcwf_col_fld'>
                      <div style={{display: 'flex', alignItems: 'flex-start', marginBottom: '10px'}}>
                        <input type='checkbox' id='privacyConsent1' required style={{marginRight: '8px', marginTop: '2px'}} />
                        <label htmlFor='privacyConsent1' style={{fontSize: '12px', color: '#666', lineHeight: '1.4'}}>
                          I agree to the{" "}
                          <a href='/privacy-policy' target='_blank' style={{color: '#8932c8', textDecoration: 'underline'}}>
                            Privacy Policy
                          </a>{" "}
                          and consent to the processing of my personal data. <span style={{color: 'red'}}>*</span>
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  <input type='text' style={{display: 'none'}} name='aG9uZXlwb3Q' value='' />
                  
                  <div className='zcwf_row'>
                    <div className='zcwf_col_lab'></div>
                    <div className='zcwf_col_fld'>
                      <input type='submit' id='formsubmit' className='formsubmit zcwf_button' value='Submit' style={{backgroundColor: '#8932c8', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '4px', cursor: 'pointer'}} />
                      <input type='reset' className='zcwf_button' name='reset' value='Reset' style={{marginLeft: '10px', padding: '10px 20px', borderRadius: '4px', cursor: 'pointer'}} />
                    </div>
                  </div>
                </form>
              </div>
            </DialogContent>
          </Dialog>
          <Button asChild variant="outline" className="border-[#8932c8] text-[#8932c8]">
            <Link to="/products/spharakaxdr">Explore Our Platform</Link>
          </Button>
        </div>
      </div>
    </div>
    </>
  );
};

export default XDRImplementationSection;
